import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    icon: '📁',
    color: '#3B82F6'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      
      // First, try to initialize default categories
      try {
        await axios.post(`${API}/admin/categories/init-defaults`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (initError) {
        console.log('Categories already initialized or error:', initError);
      }
      
      // Then load all categories
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('tkale_token');
      
      if (editingCategory) {
        await axios.put(
          `${API}/admin/categories/${editingCategory.category_id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Catégorie mise à jour');
      } else {
        const params = new URLSearchParams(form).toString();
        await axios.post(
          `${API}/admin/categories/create?${params}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Catégorie créée');
      }
      
      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Désactiver cette catégorie ?')) return;
    
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.delete(`${API}/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie désactivée');
      loadCategories();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '📁',
      color: category.color
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({ name: '', description: '', icon: '📁', color: '#3B82F6' });
    setEditingCategory(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button onClick={() => window.location.href = '/dashboard'} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold">Gestion des Catégories</h1>
          </div>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Catégorie
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Catégories Personnalisées</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && categories.length === 0 ? (
              <div className="text-center py-8">Chargement...</div>
            ) : categories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Aucune catégorie</div>
            ) : (
              <div className="space-y-3">
                {categories.map((cat) => (
                  <div 
                    key={cat.category_id} 
                    className="border rounded-lg p-4 hover:bg-gray-50"
                    style={{ borderLeftColor: cat.color, borderLeftWidth: '4px' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <span className="text-3xl">{cat.icon}</span>
                        <div>
                          <h3 className="font-semibold">{cat.name}</h3>
                          {cat.description && <p className="text-sm text-gray-600">{cat.description}</p>}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: cat.color }} />
                        <Button size="sm" variant="outline" onClick={() => handleEdit(cat)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(cat.category_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={(o) => { setShowModal(o); if (!o) resetForm(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Modifier' : 'Nouvelle'} Catégorie</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nom *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Icône</Label>
                  <Input value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} maxLength={2} />
                </div>
                <div>
                  <Label>Couleur</Label>
                  <Input type="color" value={form.color} onChange={(e) => setForm({...form, color: e.target.value})} />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>Annuler</Button>
                <Button type="submit" disabled={loading}>{loading ? 'Sauvegarde...' : editingCategory ? 'Modifier' : 'Créer'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CategoryManagement;
